import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLenis } from '../hooks/useLenis';
import RoadShader from '../components/RoadShader';
import PhoneMockup from '../components/PhoneMockup';

gsap.registerPlugin(ScrollTrigger);

const FORMSPREE = 'https://formspree.io/f/xjgpnpoo';

// ── Data ──────────────────────────────────────────────────────────────────────
const HEADLINE_WORDS = ['Get', 'there', 'together.'];

const HOW_STEPS = [
  { num: '01', title: 'Sign Up with .edu',    desc: 'Create your account using your university email. We verify you\'re a real student before anything else.' },
  { num: '02', title: 'Build Your Profile',   desc: 'Add your university, hometown, and a short bio. Drivers include car details for added trust.' },
  { num: '03', title: 'Find or Post a Ride',  desc: 'Browse rides by date and destination, or post your own route as a driver with open seats.' },
  { num: '04', title: 'Connect & Go',         desc: 'Request a ride, get accepted, chat with your driver, and hit the road. Simple as that.' },
];

const FEATURES = [
  { icon: '🎓', title: 'Verified .edu Only',     desc: 'Every account is verified through a real university email. You\'ll only ever ride with confirmed college students — no exceptions.' },
  { icon: '📍', title: 'Smart Route Matching',    desc: 'Browse rides filtered by destination and date. Find the perfect match for your schedule in seconds.' },
  { icon: '💬', title: 'In-App Messaging',        desc: 'Coordinate with your driver or riders directly inside WayMatch — no sharing personal numbers until you\'re comfortable.' },
  { icon: '⭐', title: 'Trust & Ratings',         desc: 'A two-way rating system keeps the community accountable. See ratings, completed rides, and badges before you book.' },
  { icon: '🚗', title: 'Driver & Rider Roles',    desc: 'Be a driver, a rider, or both. Switch roles anytime and manage all your rides from one clean dashboard.' },
  { icon: '💸', title: 'Fair Cost Sharing',       desc: 'Drivers set a suggested price per seat. No surge pricing, no commissions. Just gas money split between students.' },
];

const STATS = [
  { num: '200',   suffix: '+',   label: 'Target Universities', sub: 'At Launch' },
  { num: '247',   suffix: '+',   label: 'Waitlist Signups',    sub: 'And Growing' },
  { num: '$0',    suffix: '',    label: 'Platform Fees',       sub: 'Always Free' },
  { num: 'Q3',    suffix: "'26", label: 'Target Launch',       sub: 'Coming Soon' },
];

const DRIVER_PERKS = [
  'Post a ride in under 2 minutes',
  'Set your own price per seat',
  'Accept or decline any request',
  'Chat with riders before departure',
  'Build a trusted driver profile',
  'Track your ride history',
];

const RIDER_PERKS = [
  'Browse rides by date & destination',
  'View driver profiles & ratings',
  'Send a ride request in one tap',
  'Message your driver once matched',
  'Track upcoming & past rides',
  'Rate your experience after each trip',
];

const SCHOOLS_ROW1 = [
  'University of Texas at Austin','UCLA','University of Michigan','NYU',
  'University of Florida','Ohio State University','Penn State','University of Chicago',
  'Georgia Tech','Boston University','USC','University of Washington',
];
const SCHOOLS_ROW2 = [
  'Purdue University','University of Colorado','Arizona State University','University of Virginia',
  'NC State','University of Wisconsin','Texas A&M','Indiana University',
  'University of Maryland','Michigan State','Florida State University','University of Minnesota',
];

const VALUES = [
  { icon: '🔒', title: 'Safety First',       desc: 'Every user is verified through a real .edu email. No anonymous profiles, ever.' },
  { icon: '💛', title: 'Community Driven',   desc: 'We build features based on real student feedback, not what looks good on a pitch deck.' },
  { icon: '💸', title: 'Always Free',        desc: 'No commissions, no surge pricing. WayMatch will always be free for students to use.' },
];

const TEAM = [
  {
    initials: 'ES', name: 'Eric Soltanovich',
    color: 'linear-gradient(135deg,#3B82F6,#6366F1)',
    desc: 'Co-Founder of WayMatch. Passionate about making student travel safer, more affordable, and more connected.',
  },
  {
    initials: 'JS', name: 'Josh Solemani',
    color: 'linear-gradient(135deg,#0EA5E9,#3B82F6)',
    desc: 'Co-Founder of WayMatch. Focused on building a platform students can genuinely trust every time they ride.',
  },
];

// ── Shared style helpers ──────────────────────────────────────────────────────
const sectionWrap = {
  position: 'relative', zIndex: 1,
  padding: '88px 5%',
  background: 'rgba(13,27,46,0.92)',
  backdropFilter: 'blur(12px)',
};
const sectionAlt = { ...sectionWrap, background: 'rgba(10,18,36,0.95)' };

function SectionLabel({ children }) {
  return (
    <span style={{
      display: 'inline-block', color: '#60a5fa',
      fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
      textTransform: 'uppercase', marginBottom: 12,
    }}>{children}</span>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 style={{
      fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', fontWeight: 800,
      letterSpacing: '-0.03em', lineHeight: 1.2, marginBottom: 14,
    }}>{children}</h2>
  );
}

function SectionSub({ children, style = {} }) {
  return (
    <p style={{
      fontSize: '1.05rem', color: 'rgba(255,255,255,0.5)',
      maxWidth: 540, lineHeight: 1.7, ...style,
    }}>{children}</p>
  );
}

// ── Reveal wrapper (IntersectionObserver) ─────────────────────────────────────
function Reveal({ children, delay = 0, style = {} }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(26px)';
    el.style.transition = `opacity 0.65s ease ${delay}s, transform 0.65s ease ${delay}s`;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        el.style.opacity = '1';
        el.style.transform = 'none';
        io.disconnect();
      }
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);
  return <div ref={ref} style={style}>{children}</div>;
}

// ── Fixed landing nav ─────────────────────────────────────────────────────────
const NAV_LINKS = [
  { label: 'Features',     id: 'features' },
  { label: 'For Students', id: 'roles' },
  { label: 'Schools',      id: 'schools' },
  { label: 'About',        id: 'about' },
];

function LandingNav({ onJoinClick }) {
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, height: 58, zIndex: 20,
      display: 'flex', alignItems: 'center',
      padding: '0 5%',
      background: scrolled ? 'rgba(6,12,26,0.95)' : 'rgba(8,16,32,0.82)',
      backdropFilter: 'blur(16px)',
      borderBottom: scrolled
        ? '1px solid rgba(255,255,255,0.10)'
        : '1px solid rgba(255,255,255,0.07)',
      transition: 'background 0.3s, border-color 0.3s',
    }}>
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9,
          background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16,
        }}>🚗</div>
        <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.5px' }}>WayMatch</span>
      </div>

      {/* Center nav links — takes remaining space, hidden on mobile */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}
        className="nav-links"
      >
        {NAV_LINKS.map(link => (
          <button
            key={link.id}
            onClick={() => scrollToSection(link.id)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600,
              padding: '6px 14px', borderRadius: 8,
              transition: 'color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
              e.currentTarget.style.background = 'none';
            }}
          >
            {link.label}
          </button>
        ))}
      </div>

      {/* Right side — right column */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, justifyContent: 'flex-end' }}>
        {/* "Launching Soon" pill — hidden on mobile */}
        <span className="launch-pill" style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.28)',
          color: '#86efac', padding: '5px 12px', borderRadius: 999,
          fontSize: 11, fontWeight: 700,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', background: '#22c55e',
            animation: 'pulse 1.8s ease-in-out infinite',
          }} />
          Launching Soon
        </span>
        <button
          onClick={onJoinClick}
          style={{
            padding: '8px 18px', fontSize: 13, fontWeight: 700,
            background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
            color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(59,130,246,0.35)',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 6px 22px rgba(59,130,246,0.5)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(59,130,246,0.35)';
          }}
        >
          Join the Waitlist →
        </button>
      </div>
    </nav>
  );
}

// ── Waitlist form (Formspree) ─────────────────────────────────────────────────
function WaitlistForm({ accent = '#3b82f6', pill = false }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch(FORMSPREE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email }),
      });
      setStatus(res.ok ? 'done' : 'error');
    } catch { setStatus('error'); }
  };

  if (status === 'done') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
        borderRadius: 14, padding: '14px 20px', maxWidth: 460,
      }}>
        <span style={{ fontSize: 20 }}>🎉</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#4ade80' }}>You're on the list!</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
            We'll email you the moment WayMatch goes live.
          </div>
        </div>
      </div>
    );
  }

  const wrapStyle = pill ? {
    display: 'flex', gap: 8, maxWidth: 520,
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 14, padding: '6px 6px 6px 18px',
    transition: 'border-color 0.3s',
  } : {
    display: 'flex', gap: 8, flexWrap: 'wrap', maxWidth: 460,
  };

  return (
    <form onSubmit={handleSubmit} style={wrapStyle}>
      <input
        type="email" required placeholder="your@college.edu"
        value={email} onChange={e => setEmail(e.target.value)}
        style={{
          flex: '1 1 180px', padding: pill ? '8px 0' : '13px 16px',
          fontSize: 14, background: pill ? 'none' : 'rgba(255,255,255,0.07)',
          border: pill ? 'none' : '1px solid rgba(255,255,255,0.15)',
          borderRadius: pill ? 0 : 12, color: '#fff', outline: 'none',
          transition: 'border-color 0.15s', minWidth: 0,
        }}
        onFocus={e => { if (!pill) e.target.style.borderColor = accent; }}
        onBlur={e  => { if (!pill) e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }}
      />
      <button
        type="submit" disabled={status === 'sending'}
        style={{
          padding: pill ? '10px 22px' : '13px 24px',
          fontSize: 14, fontWeight: 700,
          background: `linear-gradient(135deg, ${accent}, #6366f1)`,
          color: '#fff', border: 'none',
          borderRadius: pill ? 10 : 12, cursor: 'pointer',
          boxShadow: '0 6px 20px rgba(59,130,246,0.3)',
          transition: 'transform 0.15s', opacity: status === 'sending' ? 0.7 : 1,
          whiteSpace: 'nowrap', flexShrink: 0,
        }}
        onMouseEnter={e => { if (status !== 'sending') e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        {status === 'sending' ? 'Joining…' : 'Join the Waitlist →'}
      </button>
      {status === 'error' && (
        <p style={{ width: '100%', fontSize: 12, color: '#f87171', marginTop: 4 }}>
          Something went wrong — please try again.
        </p>
      )}
    </form>
  );
}

// ── Waitlist counter (avatar stack + count) ───────────────────────────────────
function WaitlistCounter() {
  const avatars = [
    { initials: 'JM', bg: '#3B82F6' }, { initials: 'SR', bg: '#6366F1' },
    { initials: 'KL', bg: '#0EA5E9' }, { initials: 'AT', bg: '#8B5CF6' },
  ];
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 10,
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
      borderRadius: 999, padding: '7px 16px', marginBottom: 24,
      fontSize: 13, color: 'rgba(255,255,255,0.55)',
    }}>
      <div style={{ display: 'flex' }}>
        {avatars.map((a, i) => (
          <div key={i} style={{
            width: 24, height: 24, borderRadius: '50%',
            background: a.bg, border: '2px solid #0a1628',
            marginLeft: i === 0 ? 0 : -7,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 8, fontWeight: 700, color: '#fff',
          }}>{a.initials}</div>
        ))}
      </div>
      <span><strong style={{ color: '#fff' }}>247 students</strong> already on the waitlist</span>
    </div>
  );
}

// ── App preview: 3 floating phones ───────────────────────────────────────────
function AppPreviewSection() {
  const cardStyle = (bg) => ({
    background: bg, border: '1.5px solid rgba(255,255,255,0.10)',
    borderRadius: 28, overflow: 'hidden', flexShrink: 0,
    boxShadow: '0 30px 70px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)',
  });
  const notch = (
    <div style={{ height: 26, background: '#080f1e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 52, height: 5, background: 'rgba(255,255,255,0.12)', borderRadius: 3 }} />
    </div>
  );
  const screen = { padding: 12, background: '#0f1e38' };
  const msCard = {
    background: '#162b57', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 10, padding: '10px 11px', marginBottom: 8,
  };
  const tag = (color, text) => (
    <span style={{
      background: color, color: '#60a5fa', fontSize: 9, padding: '2px 7px',
      borderRadius: 99, fontWeight: 700, marginRight: 4,
    }}>{text}</span>
  );

  return (
    <div style={{ ...sectionAlt, borderTop: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
      <Reveal>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <SectionLabel>App Preview</SectionLabel>
          <SectionTitle>Browse. Match. <span style={{ color: '#3b82f6' }}>Ride.</span></SectionTitle>
          <SectionSub style={{ margin: '0 auto' }}>
            A clean, intuitive experience built around how students actually travel.
          </SectionSub>
        </div>
      </Reveal>

      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        gap: 20, position: 'relative',
      }}>
        {/* Left phone — Browse Rides */}
        <div style={{ ...cardStyle('#0f1e38'), width: 200, marginTop: 30, animation: 'phoneFloat 4s ease-in-out 0.5s infinite' }}>
          {notch}
          <div style={screen}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700 }}>Browse Rides</span>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>A</div>
            </div>
            {[
              { icon: '🚗', route: 'Austin → Dallas', sub: 'Fri Apr 18 · 9:00 AM', price: '$22', seats: '2 seats' },
              { icon: '🚙', route: 'Austin → Houston', sub: 'Sat Apr 19 · 2:00 PM', price: '$18', seats: '3 seats' },
            ].map(r => (
              <div key={r.route} style={msCard}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
                  <div style={{ width: 24, height: 24, borderRadius: 7, background: 'rgba(59,130,246,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>{r.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, fontWeight: 600 }}>{r.route}</div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)' }}>{r.sub}</div>
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#60a5fa' }}>{r.price}</div>
                </div>
                <div>{tag('rgba(59,130,246,0.15)', r.seats)}{tag('rgba(34,197,94,0.15)', 'Verified')}<span style={{ fontSize: 9, color: '#fcd34d' }}>★★★★★</span></div>
              </div>
            ))}
          </div>
        </div>

        {/* Center phone — Profile (taller) */}
        <div style={{ ...cardStyle('#0d1828'), width: 200, animation: 'phoneFloat 4s ease-in-out infinite' }}>
          {notch}
          <div style={{ ...screen, padding: 14 }}>
            <div style={{ textAlign: 'center', padding: '10px 0 14px' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, margin: '0 auto 8px' }}>JM</div>
              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 2 }}>Jake Martinez</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)' }}>UT Austin · Austin, TX</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'rgba(59,130,246,0.15)', color: '#60a5fa', fontSize: 8, padding: '2px 8px', borderRadius: 99, marginTop: 5, fontWeight: 600 }}>✓ Verified Student</div>
            </div>
            <div style={{ display: 'flex', gap: 7, marginBottom: 12 }}>
              {[{ n: '48', l: 'Rides' }, { n: '4.9★', l: 'Rating', c: '#fcd34d' }, { n: '2yr', l: 'Member' }].map(s => (
                <div key={s.l} style={{ flex: 1, background: '#162b57', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 7, padding: 7, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: s.c || '#fff' }}>{s.n}</div>
                  <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.4)' }}>{s.l}</div>
                </div>
              ))}
            </div>
            <div style={msCard}>
              <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.4)', marginBottom: 4, letterSpacing: '0.05em' }}>NEXT RIDE</div>
              <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 3 }}>Austin → San Antonio</div>
              <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>Fri Apr 18 · 10:30 AM · 2 seats left</div>
              <div style={{ background: '#3b82f6', borderRadius: 5, padding: '5px 0', textAlign: 'center', fontSize: 9, fontWeight: 700 }}>Manage Ride</div>
            </div>
          </div>
        </div>

        {/* Right phone — Messages */}
        <div style={{ ...cardStyle('#0f1e38'), width: 200, marginTop: 30, animation: 'phoneFloat 4s ease-in-out 1s infinite' }}>
          {notch}
          <div style={screen}>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 12 }}>💬 Messages</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <div style={{ background: '#162b57', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 9, padding: '9px 10px', display: 'flex', gap: 7, alignItems: 'center' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>SR</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 700 }}>Sara R.</div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)' }}>Sounds good, see you then!</div>
                </div>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>2m</div>
              </div>
              <div style={{ background: '#3b82f6', borderRadius: '8px 8px 2px 8px', padding: '8px 10px', fontSize: 9, marginLeft: 18 }}>Hey! I'll be at the main entrance 🙌</div>
              <div style={{ background: '#162b57', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px 8px 8px 2px', padding: '8px 10px', fontSize: 9, color: 'rgba(255,255,255,0.85)', marginRight: 18 }}>Running 5 min late — is that ok?</div>
              <div style={{ background: '#3b82f6', borderRadius: '8px 8px 2px 8px', padding: '8px 10px', fontSize: 9, marginLeft: 18 }}>No worries! 👍</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom glow */}
      <div style={{
        position: 'absolute', bottom: -20, left: '50%', transform: 'translateX(-50%)',
        width: '50%', height: 80,
        background: 'radial-gradient(ellipse, rgba(59,130,246,0.2), transparent 70%)',
        filter: 'blur(20px)', pointerEvents: 'none',
      }} />
    </div>
  );
}

// ── Stats band ────────────────────────────────────────────────────────────────
function StatsSection() {
  return (
    <div style={{
      position: 'relative', zIndex: 1,
      background: 'rgba(10,18,36,0.98)',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      padding: '48px 5%',
    }}>
      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', maxWidth: 900, margin: '0 auto' }}>
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1} style={{ flex: '1 1 180px', textAlign: 'center', padding: '16px 20px', position: 'relative' }}>
            {i > 0 && <div style={{ position: 'absolute', left: 0, top: '20%', height: '60%', width: 1, background: 'rgba(255,255,255,0.07)' }} />}
            <div style={{ fontSize: 'clamp(2rem,4vw,2.6rem)', fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 6 }}>
              {s.num}<span style={{ color: '#60a5fa' }}>{s.suffix}</span>
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 500, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 600 }}>{s.sub}</div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

// ── Features grid ─────────────────────────────────────────────────────────────
function FeaturesSection() {
  return (
    <div style={sectionWrap}>
      <Reveal style={{ textAlign: 'center', marginBottom: 52 }}>
        <SectionLabel>What's Coming</SectionLabel>
        <SectionTitle>Everything You Need,<br />Nothing You Don't</SectionTitle>
        <SectionSub style={{ margin: '0 auto' }}>
          Designed specifically for college students — not a generic rideshare app with a ".edu" sticker slapped on.
        </SectionSub>
      </Reveal>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 18, maxWidth: 960, margin: '0 auto' }}>
        {FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={(i % 3) * 0.1}>
            <div
              style={{
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 16, padding: 28, height: '100%',
                transition: 'border-color 0.3s, transform 0.3s',
                position: 'relative', overflow: 'hidden',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(59,130,246,0.35)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
                e.currentTarget.style.transform = 'none';
              }}
            >
              <div style={{ width: 48, height: 48, borderRadius: 13, background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 18 }}>{f.icon}</div>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 9 }}>{f.title}</h3>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.65 }}>{f.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

// ── Driver / Rider roles ──────────────────────────────────────────────────────
function RolesSection() {
  return (
    <div style={sectionAlt}>
      <Reveal style={{ textAlign: 'center', marginBottom: 52 }}>
        <SectionLabel>For Students</SectionLabel>
        <SectionTitle>Whether You Drive<br />or You Ride</SectionTitle>
        <SectionSub style={{ margin: '0 auto' }}>
          WayMatch works for every student — you can even switch between roles whenever you want.
        </SectionSub>
      </Reveal>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 22, maxWidth: 880, margin: '0 auto' }}>
        {/* Driver */}
        <Reveal delay={0}>
          <div style={{
            borderRadius: 16, padding: 36,
            background: 'linear-gradient(135deg, #162b57, rgba(59,130,246,0.08))',
            border: '1px solid rgba(59,130,246,0.18)', height: '100%',
          }}>
            <span style={{ fontSize: 40, display: 'block', marginBottom: 16 }}>🚗</span>
            <h3 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>For Drivers</h3>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 24 }}>Turn your commute into extra cash while helping out classmates.</p>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 11 }}>
              {DRIVER_PERKS.map(p => (
                <li key={p} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(59,130,246,0.22)', color: '#60a5fa', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>✓</span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        {/* Rider */}
        <Reveal delay={0.1}>
          <div style={{
            borderRadius: 16, padding: 36,
            background: 'linear-gradient(135deg, #0f2044, rgba(99,102,241,0.08))',
            border: '1px solid rgba(99,102,241,0.18)', height: '100%',
          }}>
            <span style={{ fontSize: 40, display: 'block', marginBottom: 16 }}>🎒</span>
            <h3 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>For Riders</h3>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 24 }}>Affordable, safe rides with verified students from your campus network.</p>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 11 }}>
              {RIDER_PERKS.map(p => (
                <li key={p} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(99,102,241,0.22)', color: '#a5b4fc', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>✓</span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

// ── Schools marquee ───────────────────────────────────────────────────────────
function SchoolsSection() {
  // Duplicate rows for seamless loop
  const row1 = [...SCHOOLS_ROW1, ...SCHOOLS_ROW1];
  const row2 = [...SCHOOLS_ROW2, ...SCHOOLS_ROW2];

  const chipStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 9,
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 999, padding: '9px 18px', whiteSpace: 'nowrap',
    fontSize: 13, fontWeight: 600, color: 'rgba(240,244,255,0.85)',
    transition: 'border-color 0.2s',
    cursor: 'default',
  };

  const Track = ({ schools, reverse }) => (
    <div style={{ overflow: 'hidden', position: 'relative', marginTop: reverse ? 12 : 0 }}>
      {/* fade edges */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 120, background: 'linear-gradient(90deg,rgba(10,18,36,0.95),transparent)', zIndex: 2, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 120, background: 'linear-gradient(270deg,rgba(10,18,36,0.95),transparent)', zIndex: 2, pointerEvents: 'none' }} />
      <div style={{
        display: 'flex', gap: 12, width: 'max-content',
        animation: `schoolsScroll${reverse ? 'Rev' : ''} ${reverse ? 28 : 32}s linear infinite`,
      }}>
        {schools.map((s, i) => (
          <div
            key={i} style={chipStyle}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
          >
            <span>🎓</span> {s}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ ...sectionWrap, overflow: 'hidden' }}>
      <Reveal style={{ textAlign: 'center', marginBottom: 48 }}>
        <SectionLabel>Schools We Serve</SectionLabel>
        <SectionTitle>Your Campus,<br />Our Community</SectionTitle>
        <SectionSub style={{ margin: '0 auto' }}>
          WayMatch is built for students across the country. If your school has a .edu email, you're in.
        </SectionSub>
      </Reveal>

      <Track schools={row1} reverse={false} />
      <Track schools={row2} reverse={true} />

      <Reveal style={{ textAlign: 'center', marginTop: 32 }}>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
          Any school with a <strong style={{ color: '#60a5fa' }}>.edu email address</strong> is welcome — not just the ones listed above.
        </p>
      </Reveal>
    </div>
  );
}

// ── About us ──────────────────────────────────────────────────────────────────
function AboutSection() {
  return (
    <div style={sectionAlt}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
        gap: 56, maxWidth: 960, margin: '0 auto', alignItems: 'start',
      }}>
        {/* Left: text + values */}
        <Reveal>
          <SectionLabel>About Us</SectionLabel>
          <h2 style={{ fontSize: 'clamp(1.8rem,3vw,2.6rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.2, marginBottom: 18 }}>
            Built by Students,<br />for Students
          </h2>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.85, marginBottom: 14 }}>
            WayMatch started with a simple frustration: college students spending a fortune on rideshares every time they needed to get home for break, while classmates were making the exact same drive with empty seats.
          </p>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.85, marginBottom: 14 }}>
            We're a small team of former college students who lived this problem firsthand. We built WayMatch to create a <strong style={{ color: '#fff' }}>trusted, student-only network</strong> where getting a ride home is as easy as finding a classmate headed the same way.
          </p>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.85, marginBottom: 28 }}>
            Every decision we make is guided by one goal — making student travel safer, cheaper, and more social.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {VALUES.map(v => (
              <div key={v.title} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>{v.icon}</div>
                <div>
                  <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 3 }}>{v.title}</h4>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.55, margin: 0 }}>{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Right: team cards */}
        <Reveal delay={0.1}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {TEAM.map(m => (
              <div
                key={m.name}
                style={{
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 14, padding: 20, display: 'flex', alignItems: 'center', gap: 15,
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(59,130,246,0.3)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
              >
                <div style={{
                  width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
                  background: m.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 800,
                }}>{m.initials}</div>
                <div>
                  <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 3 }}>{m.name}</h4>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.55, margin: 0 }}>{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  );
}

// ── Main Landing page ─────────────────────────────────────────────────────────
export default function Landing() {
  const waitlistRef = useRef(null);

  const heroRef      = useRef(null);
  const wordsRef     = useRef([]);
  const subheadRef   = useRef(null);
  const ctaRef       = useRef(null);
  const phoneWrapRef = useRef(null);
  const colorShift   = useRef(0);
  const phoneRotY    = useRef(-15);
  const act1Ref      = useRef(null);
  const act2Ref      = useRef(null);
  const act3Ref      = useRef(null);
  const stepsRef     = useRef([]);

  useLenis();

  const scrollToWaitlist = () =>
    waitlistRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // Entrance animations
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      [wordsRef.current, subheadRef.current, ctaRef.current, phoneWrapRef.current]
        .flat().filter(Boolean)
        .forEach(el => { el.style.opacity = 1; el.style.transform = 'none'; });
      return;
    }
    const ctx = gsap.context(() => {
      gsap.fromTo(wordsRef.current.filter(Boolean),
        { opacity: 0, y: 28, rotateX: -15 },
        { opacity: 1, y: 0, rotateX: 0, duration: 0.75, stagger: 0.09, ease: 'power3.out', delay: 0.3 }
      );
      gsap.fromTo([subheadRef.current, ctaRef.current].filter(Boolean),
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.12, ease: 'power2.out', delay: 0.7 }
      );
      gsap.fromTo(phoneWrapRef.current,
        { opacity: 0, x: 60, scale: 0.92 },
        { opacity: 1, x: 0, scale: 1, duration: 0.9, ease: 'power3.out', delay: 0.5 }
      );
    }, heroRef);
    return () => ctx.revert();
  }, []);

  // Cinematic scroll timeline
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.innerWidth < 768;
    if (prefersReduced || isMobile) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: '+=280%',
          pin: true,
          scrub: 1.5,
          anticipatePin: 1,
        },
      });
      tl.to(act1Ref.current, { opacity: 0, y: -32, duration: 1.2, ease: 'power2.in' }, 1.5);
      tl.to(colorShift,      { current: 0.75, duration: 2.5, ease: 'none' }, 1.5);
      tl.to(phoneRotY,       { current: 0, duration: 2.5, ease: 'power2.out' }, 1.5);
      tl.fromTo(act2Ref.current, { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 1.4, ease: 'power3.out' }, 2.6);
      tl.to(act2Ref.current, { opacity: 0, y: -32, duration: 1.2, ease: 'power2.in' }, 5.8);
      tl.to(colorShift,      { current: 0.22, duration: 1.6, ease: 'none' }, 5.8);
      tl.to(phoneRotY,       { current: -8, duration: 1.6, ease: 'power2.out' }, 5.8);
      tl.fromTo(act3Ref.current, { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 1.2, ease: 'power3.out' }, 6.8);
      tl.fromTo(stepsRef.current.filter(Boolean),
        { opacity: 0, x: -44 },
        { opacity: 1, x: 0, duration: 0.75, stagger: 0.3, ease: 'power2.out' }, 7.4);
    }, heroRef);
    return () => ctx.revert();
  }, []);

  return (
    <div style={{ minHeight: '100vh', color: '#fff' }}>
      <LandingNav onJoinClick={scrollToWaitlist} />
      <RoadShader colorShiftRef={colorShift} />

      {/* ── HERO — 3-act pinned ─────────────────────────────────────────── */}
      <div
        ref={heroRef}
        style={{
          position: 'relative', height: '100vh', zIndex: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '80px 6vw 60px', gap: '4vw', flexWrap: 'wrap', overflow: 'hidden',
        }}
      >
        <div style={{ flex: '1 1 320px', maxWidth: 480, zIndex: 2, position: 'relative', minHeight: 420, alignSelf: 'center' }}>

          {/* ACT 1 */}
          <div ref={act1Ref} style={{ position: 'absolute', top: 0, left: 0, width: '100%' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
              borderRadius: 20, padding: '5px 12px', marginBottom: 16,
              fontSize: 12, color: '#93c5fd', fontWeight: 500,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f59e0b', boxShadow: '0 0 6px #f59e0b', animation: 'pulse 2s ease-in-out infinite' }} />
              Launching soon · nationwide
            </div>

            <h1 style={{ fontSize: 'clamp(42px,6vw,72px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-2px', marginBottom: 18, perspective: 600 }}>
              {HEADLINE_WORDS.map((word, i) => (
                <span key={word} ref={el => wordsRef.current[i] = el}
                  style={{ display: 'inline-block', marginRight: '0.22em', opacity: 0, color: word === 'together.' ? '#3b82f6' : '#fff' }}>
                  {word}
                </span>
              ))}
            </h1>

            <p ref={subheadRef} style={{ fontSize: 'clamp(15px,2vw,19px)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, maxWidth: 380, marginBottom: 20, opacity: 0 }}>
              The carpooling app built exclusively for college students.
              Cheaper than Uber. Safer than Craigslist.
            </p>

            <div ref={el => { ctaRef.current = el; waitlistRef.current = el; }} style={{ opacity: 0 }}>
              <WaitlistCounter />
              <WaitlistForm pill={true} />
              <div style={{ marginTop: 14, display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                {['No spam, ever', 'Free to use', '.edu emails only'].map(t => (
                  <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: '#60a5fa', fontWeight: 700 }}>✓</span> {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* ACT 2 */}
          <div ref={act2Ref} style={{ position: 'absolute', top: 0, left: 0, width: '100%', opacity: 0 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(168,85,247,0.14)', border: '1px solid rgba(168,85,247,0.32)',
              borderRadius: 20, padding: '5px 12px', marginBottom: 28,
              fontSize: 12, color: '#d8b4fe', fontWeight: 500,
            }}>
              🌱 Built for students, by students
            </div>
            <h2 style={{ fontSize: 'clamp(38px,5.5vw,66px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-2px', marginBottom: 24 }}>
              Be first on<br /><span style={{ color: '#a855f7' }}>your campus.</span>
            </h2>
            <p style={{ fontSize: 'clamp(15px,1.8vw,18px)', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, maxWidth: 360, marginBottom: 44 }}>
              We're building something students actually need. Join the waitlist and be the first to know when WayMatch goes live.
            </p>
            <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
              {[{ val: 'Free', sub: 'always, for riders' }, { val: '.edu', sub: 'verified community', c: '#a855f7' }].map(s => (
                <div key={s.sub}>
                  <div style={{ fontSize: 'clamp(36px,5vw,54px)', fontWeight: 900, color: s.c || '#fff', lineHeight: 1, letterSpacing: '-1px' }}>{s.val}</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>{s.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ACT 3 */}
          <div ref={act3Ref} style={{ position: 'absolute', top: 0, left: 0, width: '100%', opacity: 0 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
              borderRadius: 20, padding: '5px 12px', marginBottom: 28,
              fontSize: 12, color: '#93c5fd', fontWeight: 500,
            }}>
              ✦ Simple by design
            </div>
            <h2 style={{ fontSize: 'clamp(38px,5.5vw,66px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-2px', marginBottom: 32 }}>
              How it<br /><span style={{ color: '#3b82f6' }}>works.</span>
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 32 }}>
              {HOW_STEPS.map((step, i) => (
                <div key={step.num} ref={el => stepsRef.current[i] = el}
                  style={{ display: 'flex', gap: 14, alignItems: 'flex-start', opacity: 0 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: 'rgba(59,130,246,0.14)', border: '1px solid rgba(59,130,246,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#3b82f6' }}>{step.num}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 3 }}>{step.title}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.55 }}>{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <WaitlistForm accent="#3b82f6" />
          </div>
        </div>

        {/* Phone */}
        <div ref={phoneWrapRef} style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center', opacity: 0, filter: 'drop-shadow(0 0 40px rgba(59,130,246,0.25))' }}>
          <PhoneMockup rotationRef={phoneRotY} />
        </div>
      </div>

      {/* ── BELOW FOLD ──────────────────────────────────────────────────────── */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <StatsSection />
        <div id="app-preview" style={{ scrollMarginTop: 58 }}><AppPreviewSection /></div>
        <div id="features"    style={{ scrollMarginTop: 58 }}><FeaturesSection /></div>
        <div id="roles"       style={{ scrollMarginTop: 58 }}><RolesSection /></div>
        <div id="schools"     style={{ scrollMarginTop: 58 }}><SchoolsSection /></div>
        <div id="about"       style={{ scrollMarginTop: 58 }}><AboutSection /></div>

        {/* Final waitlist CTA */}
        <div style={{
          ...sectionAlt,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          textAlign: 'center',
        }}>
          <Reveal>
            <div style={{
              maxWidth: 600, margin: '0 auto',
              background: 'linear-gradient(135deg, #162b57, rgba(59,130,246,0.1))',
              border: '1px solid rgba(59,130,246,0.22)',
              borderRadius: 22, padding: '56px 40px',
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: -80, right: -80, width: 300, height: 300, background: 'radial-gradient(circle,rgba(59,130,246,0.1),transparent 70%)', pointerEvents: 'none' }} />
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.28)',
                color: '#86efac', padding: '5px 14px', borderRadius: 999,
                fontSize: 11, fontWeight: 700, marginBottom: 22,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: 'pulse 1.8s ease-in-out infinite' }} />
                Launching Q3 2026
              </div>
              <h2 style={{ fontSize: 'clamp(1.8rem,3vw,2.4rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: 12 }}>
                Be First to Ride
              </h2>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, marginBottom: 28, maxWidth: 420, margin: '0 auto 28px' }}>
                WayMatch is currently in development. Join the waitlist and get early access when we launch — plus a <strong style={{ color: '#60a5fa' }}>Founding Member</strong> badge on your profile.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <WaitlistForm pill={true} />
              </div>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 14, position: 'relative', zIndex: 1 }}>
                No spam. No credit card. Just one email when we're ready to launch.
              </p>
            </div>
          </Reveal>

        </div>
      </div>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.8)} }
        @keyframes phoneFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes schoolsScroll { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @keyframes schoolsScrollRev { from{transform:translateX(-50%)} to{transform:translateX(0)} }
        input::placeholder { color: rgba(255,255,255,0.3) !important; }
        @media (max-width: 768px) {
          .nav-links { display: none !important; }
          .launch-pill { display: none !important; }
        }
      `}</style>
    </div>
  );
}
